import axiosInstance from '@/plugins/axios-instance'
import { getApiErrorMessage } from '@/utils/apiError'

const STORAGE_KEY = 'dcmweb'

export const EMPTY_USER = {
  e_mail: null,
  employee_id: null,
  full_name: null,
  login: null,
  token: null,
  refresh_token: null
}

let refreshPromise = null
let interceptorsInstalled = false
let crossTabStarted = false

export function readStoredUser () {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch (err) {
    return {}
  }
}

export function formatAxiosError (error) {
  return getApiErrorMessage(error)
}

/**
 * Shared refresh mutex. Always reads the freshest refresh_token from localStorage
 * so multi-tab races do not send a stale Vuex copy.
 */
export function refreshTokens (store, axios = axiosInstance) {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const stored = readStoredUser()
    const refreshToken = stored.refresh_token
    if (!refreshToken) {
      const err = new Error('Missing refresh_token')
      err.isAuthFailure = true
      throw err
    }

    try {
      const response = await axios.post('/api/refreshtoken', {
        refresh_token: refreshToken
      })
      store.dispatch('updateUserToken', { newToken: response.data })
      return response.data
    } catch (error) {
      const status = error.response && error.response.status
      if (status === 401) {
        error.isAuthFailure = true
      }
      throw error
    }
  })().finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

export function clearClientSession (store, router) {
  store.dispatch('setUserData', { userData: { ...EMPTY_USER } })
  if (router && router.currentRoute && router.currentRoute.name !== 'Login') {
    router.replace({ name: 'Login' })
  }
}

export function logout (store, router) {
  clearClientSession(store, router)
}

function shouldSkipAuthRetry (url) {
  if (!url) return false
  return url.indexOf('/api/refreshtoken') !== -1 || url.indexOf('/api/googlelogin') !== -1
}

/**
 * Install once: on 401 try refresh + retry original request; logout only if refresh is 401.
 */
export function installAuthInterceptors (axios, store, router, options = {}) {
  if (interceptorsInstalled) return
  interceptorsInstalled = true

  const onUnauthorizedUi = options.onUnauthorizedUi || (() => {})

  axios.interceptors.response.use(
    (response) => response,
    async (err) => {
      const status = err.response && err.response.status
      const config = err.config || {}
      const url = config.url || ''

      if (status !== 401) {
        return Promise.reject(err)
      }

      if (shouldSkipAuthRetry(url) || config._retried) {
        if (url.indexOf('/api/refreshtoken') !== -1 || config._retried) {
          onUnauthorizedUi()
          clearClientSession(store, router)
        }
        return Promise.reject(err)
      }

      try {
        const pair = await refreshTokens(store, axios)
        const retryConfig = {
          ...config,
          _retried: true,
          headers: {
            ...(config.headers || {}),
            Authorization: 'Bearer ' + pair.token
          }
        }
        return axios.request(retryConfig)
      } catch (refreshErr) {
        if (refreshErr.isAuthFailure) {
          onUnauthorizedUi()
          clearClientSession(store, router)
        }
        return Promise.reject(refreshErr)
      }
    }
  )
}

/**
 * Sync Vuex when another tab updates or clears dcmweb in localStorage.
 */
export function startCrossTabSync (store, router) {
  if (crossTabStarted || typeof window === 'undefined') return
  crossTabStarted = true

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return

    let data = {}
    try {
      data = event.newValue ? JSON.parse(event.newValue) : {}
    } catch (err) {
      data = {}
    }

    if (!data.token) {
      clearClientSession(store, router)
      return
    }

    const current = store.getters.getUser || {}
    if (current.token === data.token && current.refresh_token === data.refresh_token) {
      return
    }

    store.dispatch('updateUserToken', {
      newToken: {
        token: data.token,
        refresh_token: data.refresh_token
      }
    })
  })
}
